import { useQueryClient } from '@tanstack/react-query'
import { useContext, useState } from 'react'

import NAVAssignmentDropdown from 'components/brokers/needs_attention/NAVAssignmentDropdown'
import {
  filterByLabelAndExactValueCallback,
  setQueryDataCallback,
} from 'components/brokers/needs_attention/utils'
import BrokerContext from 'components/context/BrokerContext'
import WorkflowsContext from 'components/context/WorkflowsContext'
import { useGetBrokers } from 'components/hooks/needs_attention/useGetBrokers'
import ComboboxMultiSelectFilter, {
  type ComboboxFilterOption,
} from 'components/reusable/table/ComboboxMultiSelectFilter'
import { usePatchBrokersTaskWorkflows } from 'utils/web_api'
import { type BrokersBroker } from 'utils/web_api.schemas'

// Type extension for workflow assignments
interface WorkflowAssignmentPayload {
  workflow_ids: string[]
  broker_ids_to_add?: string[]
  broker_ids_to_remove?: string[]
  transition?: string
  cancellation_reason?: string
}

type AssignBrokersToWorkflowProps = {
  id: string
  assignedBrokers: BrokersBroker[]
}

const AssignBrokersToWorkflow = ({
  id,
  assignedBrokers,
}: AssignBrokersToWorkflowProps) => {
  const queryClient = useQueryClient()
  const { currentBroker } = useContext(BrokerContext)
  const { getWorkflowsQueryKey } = useContext(WorkflowsContext)
  const [query, setQuery] = useState('')

  const { data } = useGetBrokers()
  const brokers = data?.data as BrokersBroker[]

  const assignedBrokerIds = assignedBrokers.map((broker) => broker.id)

  const pinnedBrokerOptions: ComboboxFilterOption<string>[] = [
    ...(currentBroker
      ? [{ label: currentBroker?.broker_name, value: currentBroker?.id }]
      : []),
  ]

  const brokerOptions: ComboboxFilterOption<string>[] =
    brokers
      ?.map((broker) => ({
        label: broker.broker_name,
        value: broker.id,
      }))
      .filter((option) => option.value !== currentBroker?.id) || []

  const addAssigneesToWorkflowMutation = usePatchBrokersTaskWorkflows()

  const handleSelectAssignee = (brokerId: string) => {
    const payload: WorkflowAssignmentPayload = {
      workflow_ids: [id],
      // If the broker is already assigned, remove them, otherwise add them. We return the updated workflow
      // with the response so the assignedBrokerIds doesn't get out of sync.
      ...(assignedBrokerIds.includes(brokerId)
        ? { broker_ids_to_remove: [brokerId] }
        : { broker_ids_to_add: [brokerId] }),
    }

    addAssigneesToWorkflowMutation.mutate(
      {
        data: payload as any, // Type assertion to work around generated type limitations
      },
      {
        onSuccess: (data) => {
          // Update cached data with new Workflows that include updated assignees.
          queryClient.setQueryData(getWorkflowsQueryKey, setQueryDataCallback(data))
        },
      }
    )
  }

  const filteredPinnedBrokerOptions = pinnedBrokerOptions.filter(
    filterByLabelAndExactValueCallback(query)
  )
  const filteredBrokerOptions = brokerOptions.filter(
    filterByLabelAndExactValueCallback(query)
  )

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <ComboboxMultiSelectFilter<string>
        selectedValues={assignedBrokerIds}
        options={[
          { groupLabel: 'Pinned', options: filteredPinnedBrokerOptions },
          { groupLabel: 'Brokers', options: filteredBrokerOptions },
        ]}
        onSelectValue={handleSelectAssignee}
        placeholder='Search brokers to assign...'
        onValueChange={setQuery}
      >
        <NAVAssignmentDropdown
          noSelectionText='Assignee'
          pluralIdentifier='assignees'
          selectedValues={assignedBrokerIds}
          options={[...pinnedBrokerOptions, ...brokerOptions]}
          isPending={addAssigneesToWorkflowMutation.isPending}
          type='assignee'
        />
      </ComboboxMultiSelectFilter>
    </div>
  )
}

export default AssignBrokersToWorkflow
